import {
  useGetServiceTicketResourcesQuery,
  useUpsertServiceTicketMutation,
} from '@/store/api/services/operations/serviceTickets/index';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';

const UpdateServiceTicketStatus = ({ hide, props }: ModalChildrenProps) => {
  const [updateServiceTicket, { isLoading }] = useUpsertServiceTicketMutation();
  const { data: resourcesData } = useGetServiceTicketResourcesQuery();

  const serviceTicket = props?.serviceTicketData;
  const currentStatus = serviceTicket?.status || '';

  const [selectedStatus, setSelectedStatus] = useState(currentStatus);
  const initializedTicketId = useRef<string | null>(null);

  // Set selectedStatus to current status only when a new ticket is loaded
  useEffect(() => {
    if (serviceTicket?.id && serviceTicket.id !== initializedTicketId.current) {
      setSelectedStatus(currentStatus);
      initializedTicketId.current = serviceTicket.id;
    }
  }, [serviceTicket?.id, currentStatus]);

  const statuses = resourcesData?.data?.statuses || [];

  // Get all unique statuses (including current status)
  const allStatuses = useMemo(() => {
    // Use Set to remove duplicates, preserving original case from first occurrence
    const seen = new Set<string>();
    const uniqueStatuses: string[] = [];

    for (const status of statuses) {
      const normalized = status.toLowerCase();
      if (!seen.has(normalized)) {
        seen.add(normalized);
        uniqueStatuses.push(status);
      }
    }

    // Add current status if not already in the list
    const normalizedCurrent = currentStatus?.toLowerCase() || '';
    const statusNormalizedList = uniqueStatuses.map((s) => s.toLowerCase());
    if (currentStatus && normalizedCurrent && !statusNormalizedList.includes(normalizedCurrent)) {
      uniqueStatuses.push(currentStatus);
    }

    return uniqueStatuses;
  }, [statuses, currentStatus]);

  const normalizeStatusForApi = (status: string): string => {
    // Convert PascalCase/camelCase to snake_case and lowercase
    return status
      .replace(/([A-Z])/g, (match, _, offset) => (offset > 0 ? '_' : '') + match.toLowerCase())
      .toLowerCase();
  };

  const handleUpdateStatus = async () => {
    if (!serviceTicket?.id) {
      toast.error('Service ticket ID is required');
      return;
    }

    if (!selectedStatus) {
      toast.error('Please select a new status');
      return;
    }

    // Check if selected status is the same as current status (case-insensitive)
    if (selectedStatus.toLowerCase() === currentStatus.toLowerCase()) {
      toast.error('Please select a different status');
      return;
    }

    try {
      // Normalize status to snake_case lowercase for API
      const normalizedStatus = normalizeStatusForApi(selectedStatus);

      // Update only the status, keeping all other fields the same
      await updateServiceTicket({
        id: serviceTicket.id,
        status: normalizedStatus,
        case_id: serviceTicket.case_id || '',
        location_id: serviceTicket.location_id,
        task_types: serviceTicket.task_types || [],
        start_date: serviceTicket.start_date || new Date().toISOString(),
        technician_id: serviceTicket.technician_id ? Number(serviceTicket.technician_id) : 0,
        details: serviceTicket.details || serviceTicket.description || '',
        priority: serviceTicket.priority || 'low',
        service_window: serviceTicket.service_window || '',
      }).unwrap();

      toast.success('Service ticket status updated successfully');
      hide();
    } catch (error: any) {
      const errorMessage =
        error?.meta?.message ||
        error?.data?.message ||
        error?.message ||
        'Failed to update service ticket status';
      toast.error(errorMessage);
    }
  };

  const formatStatus = (status: string): string => {
    // Convert PascalCase/camelCase to snake_case first, then normalize
    const normalized = status
      .replace(/([A-Z])/g, (match, _, offset) => (offset > 0 ? '_' : '') + match.toLowerCase())
      .toLowerCase();

    // Custom labels for known statuses (matching API enum)
    const statusLabels: Record<string, string> = {
      planned: 'Planned',
      in_progress: 'In Progress',
      completed: 'Completed',
      failed: 'Failed',
      cancelled: 'Cancelled',
    };

    return (
      statusLabels[normalized] ||
      normalized
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    );
  };

  return (
    <>
      <DialogContent sx={{ my: 2 }}>
        {/* New Status Selection */}
        <FormGroup>
          <FormControl fullWidth size="small">
            <InputLabel>New Status</InputLabel>
            <Select
              variant="outlined"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              label="New Status"
            >
              {allStatuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {formatStatus(status)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FormGroup>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button variant="text" onClick={hide} disabled={isLoading}>
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleUpdateStatus}
          disabled={
            isLoading ||
            !selectedStatus ||
            selectedStatus.toLowerCase() === currentStatus.toLowerCase()
          }
        >
          {isLoading ? 'Updating...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </>
  );
};

export default UpdateServiceTicketStatus;
